import { useMemo } from 'react';
import { ClientProps } from '@/types';
import { useCorporateFlags } from './CorporateFlagsContext';
import { useStock } from './StockContext';
import { useSelectedClientScopeWithFallback } from './useSelectedClientScope';

const toNumber = (value: unknown) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const getSegmentCode = (segmento: any) =>
  toNumber(segmento?.cod_segmento ?? segmento?.COD_SEGMENTO ?? segmento);

/**
 * Cliente sintético para validação de preço (priceValidator / filtros no card),
 * alinhado à loja da bandeira (`activeStoreCode`) e ao segmento do pair — não ao cliente raiz do fallback.
 */
export function usePricingValidationClient(): {
  pricingClient: ClientProps;
  catalogScopeKey: string;
  clientLojaForPrice: number | null;
  clientSegsForPrice: number[];
} {
  const selectedClientScope = useSelectedClientScopeWithFallback();
  const { activeStoreCode } = useStock();
  const { selectedBrandPair } = useCorporateFlags();

  const clientSegs = useMemo(() => {
    return (Array.isArray(selectedClientScope?.COD_SEGMENTO) ? selectedClientScope.COD_SEGMENTO : [])
      .map((segmento: any) => getSegmentCode(segmento))
      .filter((segmento): segmento is number => segmento != null);
  }, [selectedClientScope?.COD_SEGMENTO]);

  const clientLojaForPrice = useMemo(() => {
    const fromStock = Number(activeStoreCode);
    if (Number.isFinite(fromStock) && fromStock > 0) {
      return fromStock;
    }
    return toNumber(selectedClientScope?.COD_LOJA);
  }, [activeStoreCode, selectedClientScope?.COD_LOJA]);

  const clientSegsForPrice = useMemo(() => {
    const seg = selectedBrandPair?.codSegmento != null ? Number(selectedBrandPair.codSegmento) : NaN;
    if (Number.isFinite(seg) && seg > 0) {
      return [seg];
    }
    return clientSegs;
  }, [selectedBrandPair?.codSegmento, clientSegs]);

  const pricingClient = useMemo((): ClientProps => {
    const segmentField =
      clientSegsForPrice.length > 0
        ? (clientSegsForPrice.map((s) => ({ cod_segmento: s })) as ClientProps['COD_SEGMENTO'])
        : selectedClientScope?.COD_SEGMENTO;

    const loja =
      clientLojaForPrice != null && Number.isFinite(clientLojaForPrice) && clientLojaForPrice > 0
        ? clientLojaForPrice
        : selectedClientScope?.COD_LOJA;

    return {
      ...selectedClientScope,
      COD_LOJA: loja as ClientProps['COD_LOJA'],
      COD_SEGMENTO: segmentField,
    } as ClientProps;
  }, [selectedClientScope, clientLojaForPrice, clientSegsForPrice]);

  const catalogScopeKey = useMemo(
    () => `${clientLojaForPrice ?? selectedClientScope?.COD_LOJA ?? ''}:${clientSegsForPrice[0] ?? ''}`,
    [clientLojaForPrice, selectedClientScope?.COD_LOJA, clientSegsForPrice]
  );

  return { pricingClient, catalogScopeKey, clientLojaForPrice, clientSegsForPrice };
}
